import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import FilterDropdown, { DARK_THEME, FilterDropdownTheme } from 'components/search-bar/advanced-search/filter-dropdown';
import ButtonGroup from 'components/control/button-group';
import Checkbox from 'components/control/checkbox';
import { searchFilterIds } from 'constants/test-constants';
import { getLocalizedBedroomBathroomParkingOptions } from 'utils/localized-select-options';
import { parameterContainsIsMore, toggleContainsMore } from 'utils/listing-query-helper';
import BedIcon from 'components/icon/filters/bed-icon';
import BathIcon from 'components/icon/filters/bath-icon';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';

import type { Bedroom, Bathroom } from 'contexts/preferences/listing-params/types';

//#region Constants
const BedroomsRegex = new RegExp(/^[0-6]\+?$/);
//#endregion

//#region Types
export type Bedrooms = `${Bedroom}` | `${Bedroom}+`;
export type Bathrooms = `${Bathroom}` | `${Bathroom}+`;

export interface BedBathFilterDropdownProps {
  bedrooms: Bedrooms;
  bathrooms: Bathrooms;
  theme?: FilterDropdownTheme;
  onBedroomsFilterChange(value: string, checkedUseExact?: boolean): void;
  onBathroomsFilterChange(value: string, checkedUseExact?: boolean): void;
}
//#endregion

const BedBathFilterDropdown = ({ bedrooms, bathrooms, onBedroomsFilterChange, onBathroomsFilterChange, theme = DARK_THEME }: BedBathFilterDropdownProps) => {
  const { t } = useTranslation(Namespaces.SEARCH);
  
  const sanitizedBedrooms = useMemo(() => {
    return BedroomsRegex.test(bedrooms) ? bedrooms : '0+';
  }, [bedrooms]);

  const sanitizedBathrooms = useMemo(() => {
    return BedroomsRegex.test(bathrooms) ? bathrooms : '0+';
  }, [bathrooms]);

  const [isExactBedroomNumber, setIsExactBedroomNumber] = useState(!parameterContainsIsMore(sanitizedBedrooms));
  const bedroomNumberOptions = getLocalizedBedroomBathroomParkingOptions(t, isExactBedroomNumber);
  const [isExactBathroomNumber, setIsExactBathroomNumber] = useState(!parameterContainsIsMore(sanitizedBathrooms));
  const bathroomNumberOptions = getLocalizedBedroomBathroomParkingOptions(t, isExactBathroomNumber);

  const combinedFilterLabel = useMemo(() => {
    return (
      <span>
        <BedIcon /> {sanitizedBedrooms} {t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BEDS, { defaultValue: 'Beds' })}, <BathIcon /> {sanitizedBathrooms} {t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BATHS, { defaultValue: 'Baths' })}
      </span>
    );
  }, [sanitizedBedrooms, sanitizedBathrooms, t]);

  const defaultValue = '0+';

  return (
    <FilterDropdown
      theme={theme}
      label={combinedFilterLabel}
      testId={searchFilterIds.bedsDropdownMap}
      isDefaultValue={bedrooms === defaultValue && bathrooms === defaultValue}
    >
      <>
        <p>{t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BEDROOMS, { defaultValue: 'Bedrooms' })}</p>
        <ButtonGroup
          options={bedroomNumberOptions}
          value={sanitizedBedrooms}
          onValueChange={onBedroomsFilterChange}
          size={'small'}
        />
        <Checkbox
          label={t(LOCALE_SEARCH_PATHS.FILTER_LABELS_USE_EXACT_NUMBER, { defaultValue: 'Use exact number' })}
          name="beds"
          value={isExactBedroomNumber}
          onValueChange={checked => {
            const toggledCheckbox = isExactBedroomNumber != checked;
            if (sanitizedBedrooms !== '0+') {
              onBedroomsFilterChange(toggleContainsMore(bedrooms), toggledCheckbox);
            }
            setIsExactBedroomNumber(checked);
          }}
          testId={searchFilterIds.bedsCheckboxSavedSearches}
        />
        <p>{t(LOCALE_SEARCH_PATHS.FILTER_LABELS_BATHROOMS, { defaultValue: 'Bathrooms' })}</p>
        <ButtonGroup
          options={bathroomNumberOptions}
          value={sanitizedBathrooms}
          onValueChange={onBathroomsFilterChange}
          size={'small'}
        />
        <Checkbox
          label={t(LOCALE_SEARCH_PATHS.FILTER_LABELS_USE_EXACT_NUMBER, { defaultValue: 'Use exact number' })}
          name="baths"
          value={isExactBathroomNumber}
          onValueChange={checked => {
            const toggledCheckbox = isExactBedroomNumber != checked;
            if (sanitizedBathrooms !== '0+') {
              onBathroomsFilterChange(toggleContainsMore(bathrooms), toggledCheckbox);
            }
            setIsExactBathroomNumber(checked);
          }}
          testId={searchFilterIds.bedsCheckboxSavedSearches}
        />
      </>
    </FilterDropdown>
  );
};

export default BedBathFilterDropdown;